import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Zap, Plus, Search, Filter, MoreHorizontal, Play, Pause,
  Trash2, Copy, Edit, Eye, Activity, ChevronLeft, Loader2,
  Bot, BookOpen, Settings, ScrollText, FlaskConical,
  CheckCircle2, AlertCircle, Clock, ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../lib/utils';
import { AUTOMATION_STATUSES, AUTOMATION_CATEGORIES, TRIGGER_TYPES, ACTION_TYPES } from './automationConfig';
import { AutomationBuilder } from './AutomationBuilder';
import { AutomationTemplateLibrary } from './AutomationTemplateLibrary';
import { AutomationLogsViewer } from './AutomationLogsViewer';
import { AIAutomationBuilder } from './AIAutomationBuilder';
import { AutomationSettingsPanel } from './AutomationSettingsPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AutomationCenter = ({ onBack }) => {
  // State
  const [automations, setAutomations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Modal states
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAIBuilder, setShowAIBuilder] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [selectedAutomationLogs, setSelectedAutomationLogs] = useState(null);

  // Fetch automations
  const fetchAutomations = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (categoryFilter && categoryFilter !== 'all') params.append('category', categoryFilter);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(`${API}/automations?${params.toString()}`);
      setAutomations(response.data);
    } catch (error) {
      console.error('Error fetching automations:', error);
      toast.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, searchQuery]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/automations/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchAutomations();
    fetchStats();
  }, [fetchAutomations, fetchStats]);

  // Toggle automation status
  const toggleStatus = async (automation) => {
    const newStatus = automation.status === 'active' ? 'paused' : 'active';
    try {
      await axios.patch(`${API}/automations/${automation.id}`, { status: newStatus });
      toast.success(`Automation ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchAutomations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to update automation');
    }
  };

  // Clone automation
  const cloneAutomation = async (automation) => {
    try {
      await axios.post(`${API}/automations/${automation.id}/clone`);
      toast.success('Automation cloned');
      fetchAutomations();
    } catch (error) {
      toast.error('Failed to clone automation');
    }
  };

  // Delete automation
  const deleteAutomation = async (automation) => {
    if (!window.confirm(`Are you sure you want to archive "${automation.name}"?`)) return;
    try {
      await axios.delete(`${API}/automations/${automation.id}`);
      toast.success('Automation archived');
      fetchAutomations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to archive automation');
    }
  };

  // Test automation
  const testAutomation = async (automation) => {
    try {
      const response = await axios.post(`${API}/automations/${automation.id}/test`);
      toast.success('Test execution started');
    } catch (error) {
      toast.error('Failed to start test');
    }
  };

  // Handle builder save
  const handleBuilderSave = async (automationData) => {
    try {
      if (editingAutomation?.id) {
        await axios.patch(`${API}/automations/${editingAutomation.id}`, automationData);
        toast.success('Automation updated');
      } else {
        await axios.post(`${API}/automations`, automationData);
        toast.success('Automation created');
      }
      setShowBuilder(false);
      setEditingAutomation(null);
      fetchAutomations();
      fetchStats();
    } catch (error) {
      toast.error('Failed to save automation');
    }
  };

  // Handle template select
  const handleTemplateSelect = (template) => {
    setEditingAutomation({
      ...template,
      id: null,
      name: `${template.name} (Copy)`,
      status: 'draft'
    });
    setShowTemplates(false);
    setShowBuilder(true);
  };

  // Handle AI builder result
  const handleAIBuilderResult = (automationConfig) => {
    setEditingAutomation({
      ...automationConfig,
      id: null,
      status: 'draft'
    });
    setShowAIBuilder(false);
    setShowBuilder(true);
  };

  const getHealthColor = (health) => {
    switch (health) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-slate-400';
    }
  };

  // If showing builder
  if (showBuilder) {
    return (
      <AutomationBuilder
        automation={editingAutomation}
        onSave={handleBuilderSave}
        onCancel={() => {
          setShowBuilder(false);
          setEditingAutomation(null);
        }}
      />
    );
  }

  // If showing templates
  if (showTemplates) {
    return (
      <AutomationTemplateLibrary
        onSelect={handleTemplateSelect}
        onBack={() => setShowTemplates(false)}
      />
    );
  }

  // If showing AI builder
  if (showAIBuilder) {
    return (
      <AIAutomationBuilder
        onResult={handleAIBuilderResult}
        onBack={() => setShowAIBuilder(false)}
      />
    );
  }

  // If showing logs
  if (showLogs) {
    return (
      <AutomationLogsViewer
        automationId={selectedAutomationLogs}
        onBack={() => {
          setShowLogs(false);
          setSelectedAutomationLogs(null);
        }}
      />
    );
  }

  // If showing settings
  if (showSettings) {
    return (
      <AutomationSettingsPanel
        onBack={() => setShowSettings(false)}
      />
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Zap className="h-6 w-6 text-yellow-500" />
              Automation Center
            </h1>
            <p className="text-sm text-muted-foreground">Manage your automations and workflows</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setShowLogs(true); setSelectedAutomationLogs(null); }}>
            <ScrollText className="h-4 w-4 mr-2" />
            Logs
          </Button>
          <Button onClick={() => { setEditingAutomation(null); setShowBuilder(true); }}>
            <Plus className="h-4 w-4 mr-2" />
            Create Automation
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-6 gap-4">
        <Card className="col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">{stats?.total_active || 0}</div>
              <div className="text-sm text-muted-foreground">Active</div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">{stats?.executions_today || 0}</div>
              <div className="text-sm text-muted-foreground">Runs Today</div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">{stats?.total_paused || 0}</div>
              <div className="text-sm text-muted-foreground">Paused</div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-slate-600">{stats?.total_draft || 0}</div>
              <div className="text-sm text-muted-foreground">Drafts</div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className={cn("text-3xl font-bold", stats?.errors_today > 0 ? "text-red-600" : "text-green-600")}>
                {stats?.errors_today || 0}
              </div>
              <div className="text-sm text-muted-foreground">Errors</div>
            </div>
          </CardContent>
        </Card>
        <Card className="col-span-1">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2">
                <div className={cn("w-3 h-3 rounded-full", getHealthColor(stats?.system_health))} />
                <span className="text-lg font-semibold capitalize">{stats?.system_health || 'Unknown'}</span>
              </div>
              <div className="text-sm text-muted-foreground">Health</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button variant="outline" onClick={() => setShowTemplates(true)}>
          <BookOpen className="h-4 w-4 mr-2" />
          Template Library
        </Button>
        <Button variant="outline" onClick={() => setShowAIBuilder(true)}>
          <Bot className="h-4 w-4 mr-2" />
          AI Builder
        </Button>
        <Button variant="outline">
          <FlaskConical className="h-4 w-4 mr-2" />
          Sandbox Mode
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search automations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(AUTOMATION_STATUSES).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {AUTOMATION_CATEGORIES.map(cat => (
              <SelectItem key={cat.id} value={cat.id}>{cat.icon} {cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Automations Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : automations.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Zap className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-lg font-medium">No automations yet</p>
              <p className="text-sm">Create your first automation to get started</p>
              <Button onClick={() => { setEditingAutomation(null); setShowBuilder(true); }} className="mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Create Automation
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Triggers</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Executions</TableHead>
                  <TableHead>Last Run</TableHead>
                  <TableHead className="w-[100px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {automations.map(automation => {
                  const statusConfig = AUTOMATION_STATUSES[automation.status] || AUTOMATION_STATUSES.draft;
                  return (
                    <TableRow key={automation.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{automation.name}</div>
                          {automation.description && (
                            <div className="text-xs text-muted-foreground truncate max-w-[300px]">
                              {automation.description}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {automation.automation_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {automation.triggers?.slice(0, 2).map((trigger, idx) => {
                            const triggerConfig = TRIGGER_TYPES[trigger.type];
                            const Icon = triggerConfig?.icon || Zap;
                            return (
                              <Badge key={idx} variant="secondary" className={cn("text-xs", triggerConfig?.color)}>
                                <Icon className="h-3 w-3 mr-1" />
                                {triggerConfig?.label || trigger.type}
                              </Badge>
                            );
                          })}
                          {automation.triggers?.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{automation.triggers.length - 2}</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <span className="text-green-600">{automation.success_count || 0}</span>
                          {' / '}
                          <span className="text-red-600">{automation.failure_count || 0}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {automation.last_executed_at
                          ? format(new Date(automation.last_executed_at), 'MMM d, h:mm a')
                          : 'Never'
                        }
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => { setEditingAutomation(automation); setShowBuilder(true); }}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleStatus(automation)}>
                              {automation.status === 'active' ? (
                                <><Pause className="h-4 w-4 mr-2" /> Pause</>
                              ) : (
                                <><Play className="h-4 w-4 mr-2" /> Activate</>
                              )}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => testAutomation(automation)}>
                              <FlaskConical className="h-4 w-4 mr-2" />
                              Test Run
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => { setSelectedAutomationLogs(automation.id); setShowLogs(true); }}>
                              <Activity className="h-4 w-4 mr-2" />
                              View Logs
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => cloneAutomation(automation)}>
                              <Copy className="h-4 w-4 mr-2" />
                              Clone
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => deleteAutomation(automation)} className="text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" />
                              Archive
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
